import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play } from 'lucide-react';
import { creativePartnerShowcaseVideos } from '@/data/creativePartnerSegments';

import { TwinneeLogo } from '@/components/TwinneeLogo';
function ShowcaseClip({ movie, index }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    el.muted = true;
    const play = () => el.play().catch(() => {});
    play();
    el.addEventListener('loadeddata', play);
    return () => el.removeEventListener('loadeddata', play);
  }, [movie.videoUrl]);

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: (index % 4) * 0.05 }}
      className="group relative overflow-hidden rounded-[20px] border border-white/10 bg-white/[0.02] hover:border-[#2DD4BF]/50 transition-all duration-300 backdrop-blur-xl shadow-xl hover:shadow-[0_0_30px_rgba(45,212,191,0.15)] hover:-translate-y-1"
      data-testid={`cpp-showcase-${movie.id}`}
    >
      <div className="relative aspect-video overflow-hidden bg-black/80">
        <video
          ref={videoRef}
          src={movie.videoUrl}
          muted
          loop
          playsInline
          autoPlay
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          aria-label={movie.title}
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

        {/* Hover Play Indicator */}
        <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/60 border border-white/20 flex items-center justify-center backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity">
          <Play className="w-3 h-3 text-[#62FAE3] fill-[#62FAE3]" />
        </div>

        <div className="pointer-events-none absolute bottom-3 left-3.5 right-3.5">
          <p className="font-sora font-bold text-sm text-white tracking-tight line-clamp-1 drop-shadow-md">
            {movie.title}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-[11px] font-mono font-medium text-[#62FAE3]">
              {movie.style}
            </span>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export function CppShowcaseVideos() {
  return (
    <section
      className="py-14 sm:py-20 border-t border-white/[0.06] relative z-10"
      data-testid="cpp-runway-showcase"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-10 sm:mb-12 space-y-2"
        >
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#2DD4BF]/10 border border-[#2DD4BF]/25 text-[#62FAE3] text-[11px] font-mono font-bold tracking-wider uppercase">
            <TwinneeLogo className="w-3.5 h-3.5" />
            SHOWCASE REEL
          </div>
          <h2 className="font-sora font-black text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">
            Built for Cinematic AI Storytellers
          </h2>
          <p className="text-xs sm:text-sm text-[#9CA3AF] font-jakarta leading-relaxed">
            Partners ship scene-native stories with Runway and multi-provider pipelines.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
          {creativePartnerShowcaseVideos.map((movie, i) => (
            <ShowcaseClip key={movie.id} movie={movie} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default CppShowcaseVideos;
